import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import ImageUploader from "@/components/ImageUploader";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";

const CATEGORIES = [
  "winners", "announcements", "verified", "spotlight",
  "producers", "creators", "crews", "platform", "magazine",
];

// Reuse the autocomplete pattern from CrewSeederRepairPanel for source-artist picker
const UserPicker = ({ label, selected, onChange, testIdPrefix }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selected) return;
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/referrals/search", { params: { q, filter: "all", limit: 6 } });
        if (!cancelled) setResults(r.data.rows || []);
      } catch { if (!cancelled) setResults([]); }
      finally { if (!cancelled) setLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, selected]);

  if (selected) {
    return (
      <div>
        <Label className="text-xs text-gray-400 block mb-1">{label}</Label>
        <div className="flex items-center gap-2 p-2 rounded bg-emerald-500/10 border border-emerald-500/40">
          <img src={getProfilePhotoUrl(selected)} alt={selected.artist_name} className="w-8 h-8 rounded-full object-cover" />
          <div className="flex-1 min-w-0 text-sm text-white truncate">{selected.artist_name}</div>
          <Button size="sm" variant="outline" onClick={() => { onChange(null); setSearch(""); }} className="border-gray-700 text-gray-300" data-testid={`${testIdPrefix}-clear`}>
            Change
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="relative">
      <Label className="text-xs text-gray-400 block mb-1">{label}</Label>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search artists (optional)"
        className="bg-black/40 border-gray-700 text-white"
        data-testid={`${testIdPrefix}-search`}
      />
      {(loading || results.length > 0) && (
        <div className="absolute left-0 right-0 mt-1 bg-[#0e0e16] border border-purple-500/40 rounded-lg shadow-xl max-h-56 overflow-y-auto z-10">
          {loading ? <div className="p-3 text-xs text-gray-400">Searching…</div> :
            results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => { onChange(u); setSearch(u.artist_name || ""); setResults([]); }}
                className="w-full text-left flex items-center gap-2 p-2 hover:bg-purple-500/10 border-b border-gray-800 last:border-b-0"
                data-testid={`${testIdPrefix}-result-${u.user_id}`}
              >
                <img src={getProfilePhotoUrl(u)} alt={u.artist_name} className="w-7 h-7 rounded-full object-cover" />
                <div className="flex-1 min-w-0 text-sm text-white truncate">{u.artist_name}</div>
              </button>
            ))
          }
        </div>
      )}
    </div>
  );
};

const formatDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return String(d).slice(0, 10); }
};

const AdminReportsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [editingId, setEditingId] = useState(null);   // Phase 18.0 — edit existing report
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [category, setCategory] = useState("announcements");
  const [isFeatured, setIsFeatured] = useState(false);
  const [sourceUser, setSourceUser] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        if (!me.data || (me.data.role !== "admin" && me.data.role !== "ar" && me.data.role !== "moderator")) {
          toast.error("Admin access required");
          navigate("/dashboard");
          return;
        }
        setUser(me.data);
      } catch {
        navigate("/login");
      }
    })();
  }, [navigate]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports", { params: { category: "all", limit: 50 } });
      setReports(r.data.reports || []);
    } catch { setReports([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) fetchReports(); }, [user, fetchReports]);

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setDescription("");
    setCoverImage("");
    setCategory("announcements");
    setIsFeatured(false);
    setSourceUser(null);
  };

  // Phase 18.0 — Open an existing report in the editor form. We pre-fill
  // every field, switch the submit button to PATCH mode, and scroll the
  // user to the top so the editor is visible.
  const handleEdit = (r) => {
    setEditingId(r.report_id);
    setTitle(r.title || "");
    setDescription(r.description || "");
    setCoverImage(r.cover_image || "");
    setCategory(r.category || "announcements");
    setIsFeatured(!!r.is_featured);
    setSourceUser(r.source_user_id ? {
      user_id: r.source_user_id,
      artist_name: r.source_user_name || r.source_user_id,
    } : null);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info(`Editing "${r.title}"`);
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!CATEGORIES.includes(category)) { toast.error("Pick a category"); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        cover_image: coverImage.trim() || null,
        category,
        is_featured: isFeatured,
        source_user_id: sourceUser?.user_id || null,
      };
      if (editingId) {
        // Update the SAME report — no duplicate insert. Status preserved.
        await api.patch(`/reports/${editingId}`, payload);
        toast.success("Report updated");
      } else {
        await api.post("/reports", { ...payload, status: "published" });
        toast.success("Report published");
      }
      resetForm();
      await fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || (editingId ? "Failed to update" : "Failed to publish"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFeatured = async (r) => {
    try {
      await api.patch(`/reports/${r.report_id}`, { is_featured: !r.is_featured });
      await fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/reports/${r.report_id}`);
      toast.success("Deleted");
      await fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  if (!user) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-reports-page">
      <SharedNavigation currentUser={user} />
      <div className="pt-20 px-4 pb-12 max-w-5xl mx-auto">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-white">📰 RGM Reports — Admin</h1>
          <p className="text-gray-400 text-sm mt-1">
            Publish platform news, winners, spotlights, verifications, and announcements.
            Automated achievement cards also land here.
          </p>
        </header>

        {/* Compose form */}
        <Card className="mb-6 p-4 bg-gradient-to-br from-purple-900/20 to-pink-900/10 border-purple-500/30">
          <h2 className="text-white font-bold mb-3" data-testid="report-form-title">
            {editingId ? `✏ Editing "${title || "report"}"` : "+ New report"}
          </h2>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-gray-400 block mb-1">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Artist of the Week: K $weet Da Goddess"
                className="bg-black/40 border-gray-700 text-white"
                data-testid="report-title-input"
                maxLength={200}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400 block mb-1">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Why does this matter? Add a few sentences…"
                className="bg-black/40 border-gray-700 text-white min-h-[100px]"
                data-testid="report-description-input"
                maxLength={4000}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-400 block mb-1">Category</Label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                  data-testid="report-category-select"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <ImageUploader
                  value={coverImage}
                  onChange={setCoverImage}
                  purpose="report"
                  label="Cover image (optional)"
                  aspect="wide"
                  helperText="Recommended 1600×900 (16:9). JPG or PNG, max 10 MB."
                  testIdPrefix="report-cover"
                />
              </div>
            </div>
            <UserPicker
              label="About artist (optional)"
              selected={sourceUser}
              onChange={setSourceUser}
              testIdPrefix="report-source-user"
            />
            <label className="flex items-center gap-2 text-xs text-amber-200 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
                className="w-4 h-4 accent-amber-500"
                data-testid="report-featured-toggle"
              />
              ★ Feature this report (pins to top of /reports and Home widget)
            </label>
            <div className="flex gap-2">
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                data-testid="report-submit-btn"
              >
                {saving
                  ? (editingId ? "Saving…" : "Publishing…")
                  : (editingId ? "💾 Save changes" : "Publish report")}
              </Button>
              <Button
                onClick={resetForm}
                disabled={saving}
                variant="outline"
                className="border-gray-700 text-gray-300"
                data-testid="report-reset-btn"
              >
                {editingId ? "Cancel edit" : "Reset"}
              </Button>
            </div>
          </div>
        </Card>

        {/* Existing reports */}
        <h2 className="text-white font-bold text-lg mb-3">Published reports ({reports.length})</h2>
        {loading ? (
          <div className="text-gray-400">Loading…</div>
        ) : reports.length === 0 ? (
          <Card className="p-6 text-center text-gray-400 bg-gray-900/40 border-gray-800">No reports yet.</Card>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => (
              <Card key={r.report_id} className="p-3 bg-gray-900/40 border-gray-800" data-testid={`admin-report-row-${r.report_id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  {r.cover_image && (
                    <img src={optimizeImage(r.cover_image, 'avatar')} alt={r.title} className="w-14 h-14 rounded object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">{r.title}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {r.category} · {formatDate(r.published_at)} · {r.is_auto ? "auto" : "manual"}
                      {r.source_user_name && <> · about <span className="text-purple-300">{r.source_user_name}</span></>}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleEdit(r)}
                    className="bg-purple-500/30 hover:bg-purple-500/50 text-purple-100 border border-purple-400/40"
                    data-testid={`edit-report-${r.report_id}`}
                  >
                    ✏ Edit
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleToggleFeatured(r)}
                    className={r.is_featured ? "bg-amber-500 text-black" : "bg-gray-800 text-gray-300"}
                    data-testid={`toggle-featured-${r.report_id}`}
                  >
                    {r.is_featured ? "★ Unpin" : "Pin"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(r)}
                    className="border-rose-500/40 text-rose-300"
                    data-testid={`delete-report-${r.report_id}`}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReportsPage;
